"use client"

import { useState, useEffect, useSyncExternalStore } from "react"
import { signIn } from "next-auth/react"
import { useSearchParams, useRouter } from "next/navigation"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { IconBrandGoogle, IconPlane, IconLoader2 } from "@tabler/icons-react"
import { getLoginErrorMessage } from "@/lib/login-errors"

// The hostname never changes while the page is open
const subscribeNever = () => () => {}

export function LoginForm({ googleEnabled }: { googleEnabled: boolean }) {
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [loading, setLoading] = useState(false)
    const [localError, setLocalError] = useState<string | null>(null)
    const searchParams = useSearchParams()
    const router = useRouter()
    const callbackUrl = searchParams.get("callbackUrl") || "/dashboard"
    const urlError = searchParams.get("error")
    const urlErrorCode = searchParams.get("code")
    // Server snapshot is false, so hydration matches; the browser value applies right after
    const isLocalhost = useSyncExternalStore(
        subscribeNever,
        () => window.location.hostname === 'localhost',
        () => false,
    )

    // Sync URL error to local state once, then allow local clearing
    useEffect(() => {
        if (urlError) {
            setLocalError(getLoginErrorMessage(urlError, urlErrorCode))
        }
    }, [urlError, urlErrorCode])

    // Clear error when user starts typing
    const clearError = () => {
        if (localError) {
            setLocalError(null)
            // Also clear URL params to prevent confusion
            if (urlError) {
                router.replace('/login' + (callbackUrl !== '/dashboard' ? `?callbackUrl=${encodeURIComponent(callbackUrl)}` : ''))
            }
        }
    }

    const handleCredentialsLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        setLocalError(null) // Clear any previous error
        setLoading(true)

        // Get client info for session tracking
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown'

        await signIn("credentials", {
            email,
            password,
            userAgent,  // Pass to auth.ts for device parsing
            callbackUrl
        })
        setLoading(false)
    }

    const handleGoogleLogin = async () => {
        if (!googleEnabled) {
            alert("Google sign-in is not configured on this server.")
            return
        }
        setLoading(true)
        await signIn("google", { callbackUrl })
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="flex justify-center mb-4">
                        <div className="rounded-full bg-primary/10 p-3">
                            <IconPlane size={32} className="text-primary" />
                        </div>
                    </div>
                    <CardTitle className="text-2xl">TourOps ERP</CardTitle>
                    <CardDescription>Sign in to your account</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {localError && (
                        <div className="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm text-center">
                            {localError}
                        </div>
                    )}

                    {/* Google Login - always shown; alerts on click if OAuth is not configured */}
                    <>
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={handleGoogleLogin}
                            disabled={loading}
                        >
                            <IconBrandGoogle size={20} className="mr-2" />
                            Continue with Google (@mushroomtravel.com)
                        </Button>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <Separator />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-background px-2 text-muted-foreground">Or</span>
                            </div>
                        </div>
                    </>

                    {/* Credentials Login */}
                    <form onSubmit={handleCredentialsLogin} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@company.com"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value)
                                    clearError()
                                }}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value)
                                    clearError()
                                }}
                                required
                            />
                        </div>
                        <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? (
                                <>
                                    <IconLoader2 size={16} className="mr-2 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                "Sign In"
                            )}
                        </Button>
                    </form>

                    {/* DEV BYPASS - Only on localhost */}
                    {isLocalhost && (
                        <div className="border-t pt-4 mt-4">
                            <Button
                                variant="ghost"
                                className="w-full text-xs text-muted-foreground hover:text-primary"
                                onClick={() => {
                                    // Auto-fill test credentials
                                    setEmail('pruetnara@softsq.com')
                                    setPassword('Test1234')
                                }}
                            >
                                🔧 DEV: Fill Test Credentials
                            </Button>
                        </div>
                    )}

                    <p className="text-xs text-muted-foreground text-center">
                        Contact your administrator if you need an account.
                    </p>
                </CardContent>
            </Card>
        </div>
    )
}
